import { useEffect, useState } from 'react';
import '../site.css';

/**
 * Das Schaufenster unter evercal.de – Aufbau und Tonfall wie evercast.video und evercourse.de (gebaut am 07.10.2026).
 * EverCal ist eine Terminbuchung nach dem Vorbild von Calendly, mit einem Unterschied, der alles prägt: **Der Kalender
 * führt.** Was in Rainers Google-Kalender steht (auch vom iPhone), ist auf der Buchungsseite gesperrt; eine Buchung
 * landet mit Meet-Link im Kalender; der Kunde bekommt genau eine Mail – im Design des Auftritts, mit Kalenderdatei.
 * Dazu: mehrere Auftritte auf einem Kalender, Steuerung per KI (Konzept 9.2), eigener Server in Deutschland,
 * Terminwunsch statt Fehlermeldung (Konzept 5.4). Wie bei den Schwestern: „kann nicht gekauft werden“, Knopf zur
 * Erfolgsmagneten-Community.
 *
 * Reihenfolge: Hero → So geht's → Was anders ist → Der Kalender führt → Eine Mail → KI → Auftritte → Bewusst nicht →
 * Funktionsumfang/Zugang → Fragen → Abschluss. Texte: „du“, entspannt, keine Angstmache, keine Absolutaussagen.
 *
 * Die Seiten werden beim Build vorgerendert (entry-server.tsx) – deshalb hier nichts, was `window` beim Rendern braucht.
 */

const COMMUNITY_URL = 'https://www.erfolgsmagneten.com';
export const LANDING_TITLE = 'EverCal – Terminbuchung, die in deinen Kalender schaut.';

/** Was EverCal heute kann – der tatsächliche Funktionsumfang, nichts Geplantes. */
const FEATURES: { title: string; items: string[] }[] = [
  {
    title: 'Dein Kalender',
    items: [
      'Google-Kalender verbinden, Zielkalender und abzugleichende Kalender ankreuzen',
      'Was im Kalender steht, ist auf der Buchungsseite gesperrt – auch Einträge vom iPhone',
      'Ganztägige Einträge sperren den Tag; eine Freigabe unter „Tage“ gewinnt',
      'Jede Buchung als Google-Termin mit Meet-Link, ohne Gast',
    ],
  },
  {
    title: 'Zeiten',
    items: [
      'Wochenraster je Terminart, zum Beispiel Di und Do 10–12 Uhr',
      'Einzelne Tage sperren oder zusätzlich freigeben',
      'Vorlauf und Horizont: wie kurzfristig und wie weit im Voraus gebucht werden darf',
      'Belegt ist belegt – über alle Terminarten hinweg, abgesichert in der Datenbank',
    ],
  },
  {
    title: 'Auftritte',
    items: [
      'Mehrere Auftritte auf einem Kalender: eigene Adresse, Logo, Farbe, Absendername',
      'Terminarten mit Dauer, Beschreibung, Google Meet oder Telefon',
      'Buchungsseite: Tag wählen, Uhrzeit wählen, Name und E-Mail – fertig',
      'Alle Zeiten in deutscher Zeit, auch über die Zeitumstellung hinweg',
    ],
  },
  {
    title: 'E-Mails',
    items: [
      'Bestätigung mit Meet-Link und Kalenderdatei zum Anklicken',
      'Erinnerung am Vortag und eine Stunde vorher',
      'Absagen und Umbuchen per persönlichem Link',
      'Nachricht an dich bei jeder Buchung, Umbuchung und Absage',
    ],
  },
  {
    title: 'Steuerung per KI',
    items: [
      'Claude verbinden – über das Netz oder lokal mit Claude Code',
      'Rechte-Stufen: nur lesen, Tage sperren und freigeben, Buchungen absagen',
      'Protokoll, wer was über die Schnittstelle geändert hat',
      'Signierte Webhooks und Lese-Schnittstelle für eigene Anbindungen',
    ],
  },
  {
    title: 'Betrieb und Datenschutz',
    items: [
      'Eigener Server in Deutschland, kein Tracking, kein Cookie-Banner',
      'Botschutz ohne Captcha und ohne Dienste Dritter',
      'Terminwunsch statt Fehlermeldung, wenn Google einmal nicht antwortet',
      'Tägliche Sicherung',
    ],
  },
];

export function LandingPage() {
  useEffect(() => {
    document.title = LANDING_TITLE;
  }, []);

  return (
    <div className="site">
      <SiteHeader />

      {/* 1. Hero */}
      <section className="site__hero">
        <h1>
          Terminbuchung, die <em>in deinen Kalender schaut.</em>
        </h1>
        <p className="site__sub">Interessenten suchen sich selbst einen freien Termin aus. Der landet mit Meet-Link in deinem Google-Kalender.</p>
        <p className="site__lead">
          Und umgekehrt: Was in deinem Kalender steht, ist auf der Buchungsseite gesperrt – auch der Zahnarzttermin, den du eben am Handy eingetragen
          hast. EverCal ist eine Terminbuchung nach dem Vorbild von Calendly, auf eigenem Server in Deutschland, steuerbar mit Claude.
        </p>
        <p className="site__claim">Du gibst Zeiten frei. Dein Kalender regelt den Rest.</p>
        <div className="site__actions">
          <a className="button button--large" href={COMMUNITY_URL} rel="noopener">
            Zur Erfolgsmagneten-Community
          </a>
        </div>
        <figure className="site__devices">
          <img className="site__desktop" src="/site/buchung.png" alt="Die Buchungsseite einer PraxiPet-Vorführung: links der Anbieter, in der Mitte der Monatskalender, rechts die freien Uhrzeiten" width={1440} height={900} />
          <img className="site__phone" src="/site/buchung-handy.png" alt="Dieselbe Buchungsseite auf dem Handy" width={390} height={844} />
        </figure>
      </section>

      {/* 2. So geht's */}
      <section id="so-gehts" className="site__steps">
        <h2>So geht's – in drei Schritten.</h2>
        <ol className="site__step-list">
          <li>
            <span className="site__step-num">1</span>
            <h3>Zeiten freigeben</h3>
            <p>Du legst ein Wochenraster fest, zum Beispiel Dienstag und Donnerstag von 10 bis 12 Uhr. Einzelne Tage sperrst du oder gibst sie zusätzlich frei.</p>
          </li>
          <li>
            <span className="site__step-num">2</span>
            <h3>Kunde wählt</h3>
            <p>Auf deiner Buchungsseite sucht sich dein Interessent einen Tag und eine Uhrzeit aus, trägt Name und E-Mail ein – und ist fertig.</p>
          </li>
          <li>
            <span className="site__step-num">3</span>
            <h3>Termin steht im Kalender</h3>
            <p>Der Termin erscheint mit Meet-Link in deinem Google-Kalender. Dein Kunde bekommt eine Bestätigung mit Link und Kalenderdatei.</p>
          </li>
        </ol>
        <p className="site__steps-claim">
          Einmal einrichten.
          <br />
          Danach buchen die anderen.
        </p>
      </section>

      {/* 3. Was EverCal anders macht */}
      <section id="anders" className="site__block">
        <h2>Was EverCal anders macht.</h2>
        <div className="site__prose">
          <p>Terminbuchungen gibt es viele. EverCal ist für Leute gebaut, die einen Kalender haben, der ohnehin stimmt – und ihn nicht noch einmal pflegen wollen.</p>
        </div>
        <div className="site__benefit-grid">
          <Benefit icon="calendar" title="Dein Kalender führt" text="Du trägst einen Termin am iPhone ein, er landet in Google, EverCal sieht ihn – und bietet die Zeit nicht mehr an. Ganztägige Einträge sperren den Tag. Gibst du einen Tag trotzdem frei, gewinnt deine Freigabe." />
          <Benefit icon="mail" title="Nur eine Mail an den Kunden" text="Dein Kunde bekommt genau eine Bestätigung – im Design deiner Marke, mit Meet-Link und Kalenderdatei. Keine zusätzliche Einladung von Google, kein zweiter Absender." />
          <Benefit icon="layers" title="Mehrere Auftritte, ein Kalender" text="PraxiPet-Vorführung und Erfolgsmagneten-Gespräch sehen nach außen wie zwei Firmen aus: eigene Adresse, eigenes Logo, eigene Farbe. Dahinter steckt ein Konto mit einem Kalender." />
          <Benefit icon="sparkle" title="Mit KI steuerbar" text="Du sagst Claude, dass du nächste Woche in Dänemark bist – und die Tage sind gesperrt. Du lässt einen Termin absagen, der Kunde bekommt deine Nachricht. Mit genau den Rechten, die du erlaubst." />
          <Benefit icon="server" title="Auf eigenem Server in Deutschland" text="EverCal läuft auf einem eigenen Server bei IONOS, ohne Tracking und ohne Cookie-Banner. Deine Buchungen liegen bei dir, nicht bei einem Anbieter in Übersee." />
          <Benefit icon="lifebuoy" title="Terminwunsch statt Fehlermeldung" text="Antwortet Google einmal nicht, zeigt EverCal keine falschen Zeiten an. Dein Interessent hinterlässt Name und E-Mail und bekommt automatisch den Link, sobald die Terminwahl wieder da ist." />
        </div>
      </section>

      {/* 4. Der Kalender führt */}
      <section className="site__block site__block--dark">
        <h2>Ein Kalender. Sonst nichts.</h2>
        <div className="site__prose">
          <p>Du pflegst deinen Kalender dort, wo du ihn ohnehin pflegst – am Handy. EverCal richtet sich danach und liest nur, von wann bis wann etwas belegt ist. Titel und Inhalt deiner Einträge bleiben bei dir. So sieht das an einem ganz normalen Dienstag aus:</p>
        </div>
        <ol className="site__simple">
          <li>iPhone</li>
          <li>Google-Kalender</li>
          <li>EverCal</li>
        </ol>
        <div className="site__day">
          <div className="site__day-head">
            <span>Dein Dienstag im Kalender</span>
            <span>Was Interessenten auf der Buchungsseite sehen</span>
          </div>
          <div className="site__day-row">
            <time>09:00</time>
            <div>
              <strong>Zahnarzt</strong>
              <small>gestern Abend am iPhone eingetragen</small>
            </div>
            <span className="site__day-out site__day-out--blocked">nicht buchbar</span>
          </div>
          <div className="site__day-row">
            <time>10:00</time>
            <div>
              <strong>frei</strong>
              <small>dein Wochenraster: dienstags 10 bis 12 Uhr</small>
            </div>
            <span className="site__day-out site__day-out--free">buchbar – als Vorführung oder Erstgespräch</span>
          </div>
          <div className="site__day-row">
            <time>11:00</time>
            <div>
              <strong>PraxiPet-Vorführung</strong>
              <small>eben gebucht – steht mit Meet-Link in deinem Kalender</small>
            </div>
            <span className="site__day-out site__day-out--blocked">nicht buchbar – auch nicht als Erstgespräch</span>
          </div>
          <div className="site__day-row">
            <time>Do–So</time>
            <div>
              <strong>Dänemark</strong>
              <small>ganztägig eingetragen</small>
            </div>
            <span className="site__day-out site__day-out--blocked">gesperrt – außer Donnerstag 10 bis 12 Uhr, das hast du unter „Tage“ freigegeben</span>
          </div>
        </div>
      </section>

      {/* 5. Eine Mail */}
      <section className="site__block">
        <h2>Eine Mail. Deine Marke. Alles drin.</h2>
        <div className="site__prose">
          <p>
            Dein Kunde sieht nur deinen Auftritt: deinen Absendernamen, deine Farbe, dein Logo. Die Bestätigung bringt den Meet-Link und eine Kalenderdatei
            mit, die er anklickt – und der Termin steht in seinem Kalender, egal ob Apple, Outlook oder Google.
          </p>
        </div>
        <div className="site__three">
          <div>
            <h3>Bestätigung</h3>
            <p>Sofort nach der Buchung: Termin, Meet-Link, Kalenderdatei, dazu die Links zum Absagen und Umbuchen.</p>
          </div>
          <div>
            <h3>Erinnerungen</h3>
            <p>Am Vortag und eine Stunde vorher – damit der Termin nicht untergeht. Beide mit dem Zugangslink.</p>
          </div>
          <div>
            <h3>Absagen und Umbuchen</h3>
            <p>Über den persönlichen Link, ohne Anmeldung. Du bekommst Bescheid, der Kalender wird mitgeführt.</p>
          </div>
        </div>
      </section>

      {/* 6. KI-Steuerung */}
      <section id="ki" className="site__block">
        <h2>Deine KI bedient die Verwaltung. Du bleibst der Chef.</h2>
        <div className="site__prose">
          <p>
            In EverCal verbindest du Claude mit deinem Konto – über das Netz oder lokal mit Claude Code – und legst fest, was er darf: nur lesen, Tage
            sperren und freigeben, Buchungen absagen. Dann redest du mit ihm wie mit einer Assistentin.
          </p>
        </div>
        <div className="site__chat">
          <div className="site__chat-msg site__chat-msg--you">
            <span>Du</span>
            <p>Sperr bitte Montag bis Mittwoch nächste Woche, ich bin in Dänemark. Und sag die PraxiPet-Vorführung am Donnerstag um 10 Uhr ab – mit einer freundlichen Nachricht an Frau Berger.</p>
          </div>
          <div className="site__chat-msg site__chat-msg--ai">
            <span>Claude</span>
            <p>
              Erledigt: Montag, 12. bis Mittwoch, 14. Oktober sind gesperrt (Grund: Dänemark). Die Vorführung am Donnerstag um 10 Uhr ist abgesagt – Frau
              Berger bekommt eine Mail mit deiner Nachricht und dem Link, um sich einen neuen Termin auszusuchen.
            </p>
          </div>
        </div>
        <div className="site__three">
          <div>
            <h3>Du bestimmst die Rechte.</h3>
            <p>Beim Verbinden setzt du Häkchen: nur lesen, Zeiten, Buchungen. Mehr bekommt die KI nicht.</p>
          </div>
          <div>
            <h3>Du siehst alles.</h3>
            <p>Ein Protokoll zeigt, wer was über die Schnittstelle geändert hat – Verbindung für Verbindung.</p>
          </div>
          <div>
            <h3>Manches bleibt bei dir.</h3>
            <p>Umbuchen, Wochenraster, Terminarten und Auftritte änderst du selbst in der Verwaltung. Das richtet man einmal ein, nicht nebenbei im Gespräch.</p>
          </div>
        </div>
        <p className="site__claim">EverCal selbst enthält keine KI. Du bringst deine mit.</p>
      </section>

      {/* 7. Zwei Auftritte */}
      <section id="auftritte" className="site__block site__block--soft">
        <h2>Zwei Auftritte. Zwei Marken. Ein Kalender.</h2>
        <div className="site__prose">
          <p>Eine PraxiPet-Interessentin sieht nichts von Erfolgsmagneten – und umgekehrt. Bucht die eine um 10 Uhr, ist 10 Uhr auch bei der anderen weg.</p>
        </div>
        <div className="site__pair">
          <figure>
            <img src="/site/auftritt-praxipet.png" alt="Terminwahl für eine PraxiPet-Vorführung im Petrol des Auftritts PraxiPet" loading="lazy" width={1100} height={720} />
            <figcaption>PraxiPet – Vorführung der Praxis-Software</figcaption>
          </figure>
          <figure>
            <img src="/site/auftritt-erfolgsmagneten.png" alt="Terminwahl für ein Erstgespräch im Rot des Auftritts Erfolgsmagneten" loading="lazy" width={1100} height={720} />
            <figcaption>Erfolgsmagneten – Erstgespräch</figcaption>
          </figure>
        </div>
      </section>

      {/* 8. Bewusst nicht */}
      <section className="site__block">
        <h2>Was EverCal bewusst nicht ist.</h2>
        <div className="site__prose">
          <p>Damit du nicht lange suchst: Ein paar Dinge, die Calendly kann, lassen wir absichtlich weg. EverCal soll klein und übersichtlich bleiben.</p>
        </div>
        <div className="site__versus">
          <div className="site__versus-col">
            <h3>Gibt es nicht</h3>
            <ul>
              <li>Bezahlung vor dem Termin</li>
              <li>Teams, Rundlauf-Verteilung, Gruppentermine</li>
              <li>Outlook-, Apple- oder andere Kalender – der Abgleich läuft nur über Google</li>
              <li>Zoom und Microsoft Teams – Treffpunkt ist Google Meet oder das Telefon</li>
              <li>Anzeige in der Zeitzone des Besuchers – die Seite zeigt deutsche Zeit und sagt das auch</li>
            </ul>
          </div>
          <div className="site__versus-col site__versus-col--us">
            <h3>Dafür gibt es</h3>
            <ul>
              <li>Einen Kalender, der führt – auch mit Einträgen vom Handy</li>
              <li>Eine Mail an den Kunden, im Design deiner Marke</li>
              <li>Mehrere Auftritte auf einem Konto</li>
              <li>Steuerung mit Claude, mit Rechten und Protokoll</li>
              <li>Einen eigenen Server in Deutschland</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 9. Funktionsumfang und Zugang – wie bei Evercast und Evercourse */}
      <section id="zugang" className="site__block">
        <h2>Was kostet dich ein Kalender, den du zweimal pflegen musst?</h2>
        <div className="site__price">
          <div className="site__price-head">
            <img className="site__logo site__logo--price" src="/site/logo.png" alt="EverCal" width={1209} height={252} loading="lazy" />
            <h3>Das kann EverCal</h3>
          </div>
          <div className="site__price-groups">
            {FEATURES.map((group) => (
              <div key={group.title}>
                <h3>{group.title}</h3>
                <ul className="site__check">
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="site__price-notice">
            <p>
              <strong>EverCal kann nicht gekauft werden.</strong> EverCal ist ein KI-Projekt der Erfolgsmagneten. Wir zeigen dir, wie einfach es heutzutage
              ist, deine eigene Terminbuchung zu bauen – und mit KI zu steuern. Wenn auch du mehr dazu wissen willst, komm in die Erfolgsmagneten-Community.
            </p>
            <a className="button button--large" href={COMMUNITY_URL} rel="noopener">
              Mehr erfahren
            </a>
          </div>
        </div>
      </section>

      {/* 10. Fragen */}
      <section id="faq" className="site__faq">
        <h2>Häufige Fragen</h2>
        <Faq q="Brauche ich ein Google-Konto?" a="Für den Meet-Link und den Abgleich der belegten Zeiten: ja, ein Google-Kalender reicht – ein normales Gmail-Konto genügt. Ohne Google läuft EverCal trotzdem, dann mit Telefon-Terminen und ohne Abgleich." />
        <Faq q="Bekommt mein Kunde eine Einladung von Google?" a="Nein. Dein Kunde steht nicht als Gast im Google-Termin. Er bekommt nur deine Bestätigungsmail – mit Meet-Link und einer Kalenderdatei, die er anklickt." />
        <Faq q="Was sieht EverCal in meinem Kalender?" a="EverCal nutzt aus den Kalendern, die du ankreuzt, nur die Zeiten: von wann bis wann etwas belegt ist. Titel und Inhalt deiner Einträge werden nicht gespeichert und nirgends angezeigt." />
        <Faq q="Was passiert, wenn Google einmal nicht antwortet?" a="Dann zeigt die Buchungsseite keine Zeiten an, sondern nimmt einen Terminwunsch mit Name und E-Mail entgegen. Sobald Google wieder antwortet, bekommt dein Interessent automatisch den Link zur Terminwahl – und du eine Nachricht." />
        <Faq q="Kann die KI etwas kaputt machen?" a="Sie kann nur, was du beim Verbinden erlaubst: lesen, Tage sperren und freigeben, Buchungen absagen. Umbuchen, Wochenraster und Terminarten bleiben in deiner Hand. Jede Änderung steht im Protokoll." />
        <Faq q="Kann ich EverCal kaufen oder mieten?" a="Nein. EverCal ist ein KI-Projekt der Erfolgsmagneten – wir zeigen damit, wie einfach es heute ist, so etwas selbst zu bauen. Wenn dich das interessiert, komm in die Erfolgsmagneten-Community." />
      </section>

      {/* 11. Abschluss */}
      <section className="site__cta">
        <h2>Warum einen Kalender zweimal pflegen?</h2>
        <p className="site__cta-sub">Terminbuchung, die in deinen Kalender schaut.</p>
        <p>EverCal ist ein KI-Projekt der Erfolgsmagneten. Wenn du wissen willst, wie so etwas entsteht, bist du in der Community richtig.</p>
        <a className="button button--large" href={COMMUNITY_URL} rel="noopener">
          Zur Erfolgsmagneten-Community
        </a>
        <p className="site__cta-note">Ein Projekt der Erfolgsmagneten.</p>
      </section>

      <SiteFooter />
    </div>
  );
}

export function LegalPage({ kind }: { kind: 'impressum' | 'datenschutz' }) {
  useEffect(() => {
    document.title = `${kind === 'impressum' ? 'Impressum' : 'Datenschutz'} – EverCal`;
  }, [kind]);
  return (
    <div className="site">
      <SiteHeader />
      <main className="site__legal">{kind === 'impressum' ? <Impressum /> : <Datenschutz />}</main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  return (
    <header className="site__bar">
      <a className="site__brand" href="/" aria-label="EverCal – zur Startseite">
        <img className="site__logo" src="/site/logo.png" alt="EverCal" width={1209} height={252} />
      </a>
      <nav className="site__nav">
        <a href="/#so-gehts">So geht's</a>
        <a href="/#anders">Was anders ist</a>
        <a href="/#ki">Mit KI</a>
        <a href="/#faq">Fragen</a>
        <a className="button button--small" href={COMMUNITY_URL} rel="noopener">
          Zur Community
        </a>
      </nav>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site__footer">
      <img className="site__logo site__logo--footer" src="/site/logo.png" alt="EverCal" width={1209} height={252} loading="lazy" />
      <div className="site__footer-links">
        <span>© 2026 EverCal · ein Projekt von Erfolgsmagneten</span>
        <a href="https://evercast.video" rel="noopener">
          Evercast – Webinare
        </a>
        <a href="https://evercourse.de" rel="noopener">
          Evercourse – Videokurse
        </a>
        <a href="/impressum">Impressum</a>
        <a href="/datenschutz">Datenschutz</a>
      </div>
    </footer>
  );
}

type IconName = 'calendar' | 'mail' | 'layers' | 'sparkle' | 'server' | 'lifebuoy';

function Benefit({ icon, title, text }: { icon: IconName; title: string; text: string }) {
  return (
    <div className="site__benefit">
      <span className="site__icon" aria-hidden="true">
        <Icon name={icon} />
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function Icon({ name }: { name: IconName }) {
  const common = { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M8 3v4M16 3v4M3 10h18M9 15l2 2 4-4" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2.5" />
          <path d="M3 8l9 6 9-6" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...common}>
          <path d="M12 3l9 5-9 5-9-5 9-5z" />
          <path d="M3 12l9 5 9-5M3 16l9 5 9-5" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg {...common}>
          <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
          <path d="M19 16l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" />
        </svg>
      );
    case 'server':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="7" rx="2" />
          <rect x="3" y="13" width="18" height="7" rx="2" />
          <path d="M7 7.5h.01M7 16.5h.01" />
        </svg>
      );
    case 'lifebuoy':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3.5" />
          <path d="M5.6 5.6l3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9" />
        </svg>
      );
  }
}

/** Aufklappbare Frage. Die Antwort steht immer im HTML (für Suchmaschinen und ohne JavaScript), zugeklappt per `hidden`. */
function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="site__faq-item">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {q}
        <span aria-hidden="true">{open ? '–' : '+'}</span>
      </button>
      <p hidden={!open}>{a}</p>
    </div>
  );
}

/** Angaben wie bei evercourse.de/impressum (gleicher Betreiber). Adresse und Register nur hier ändern. */
function Impressum() {
  return (
    <>
      <h1>Impressum</h1>
      <p className="muted">Angaben gemäß § 5 DDG</p>
      <h2>Anbieter</h2>
      <p>
        Erfolgsmagneten GmbH
        <br />
        Leopoldstr. 244
        <br />
        80807 München
      </p>
      <p>
        Vertreten durch den Geschäftsführer: Rainer Stolte
        <br />
        Registergericht: Amtsgericht München · HRB 295540
      </p>
      <h2>Kontakt</h2>
      <p>
        Telefon: <a href="tel:+498924413422">+49 (0) 89 244 134 22</a>
        <br />
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
        <br />
        Web:{' '}
        <a href="https://www.erfolgsmagneten.com" rel="noopener">
          www.erfolgsmagneten.com
        </a>
      </p>
      <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
      <p>Rainer Stolte, Anschrift wie oben.</p>
      <h2>Umsatzsteuer</h2>
      <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE370363054</p>
      <h2>Streitbeilegung</h2>
      <p>Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>
    </>
  );
}

/**
 * Datenschutzerklärung für evercal.de, aus der Evercourse-Fassung (03.10.2026) auf die Terminbuchung übertragen. Sie
 * beschreibt, was EverCal tatsächlich tut: keine Cookies und kein Tracking auf Website und Buchungsseiten; ein
 * Sitzungs-Cookie nur im Verwaltungsbereich; Buchungsdaten (Name, E-Mail, Telefon, Firma, Nachricht) zur
 * Vertragsanbahnung; Google-Kalender und Meet (nur Name, E-Mail, Termin – kein Gast); Mails über Brevo; Hosting und
 * Sicherung bei IONOS; Botschutz ohne Dienste Dritter. Wer den Code ändert, prüft, ob dieser Text noch stimmt.
 */
function Datenschutz() {
  const controller = (
    <p>
      Erfolgsmagneten GmbH
      <br />
      Leopoldstr. 244
      <br />
      80807 München
      <br />
      Deutschland
    </p>
  );
  return (
    <>
      <h1>Datenschutzerklärung</h1>
      <p className="muted">Stand: 7. Oktober 2026</p>
      <p>
        Der Schutz personenbezogener Daten ist uns wichtig. Nachfolgend informieren wir darüber, welche personenbezogenen Daten beim Besuch unserer Website
        evercal.de, bei der Buchung eines Termins über EverCal und bei der Kontaktaufnahme mit uns verarbeitet werden.
      </p>
      <p>
        Weder auf der Website noch auf den Buchungsseiten setzen wir Analyse- oder Marketingtechnologien ein. Es gibt kein Werbetracking, keine Cookies für
        Werbezwecke und kein Cookie-Banner, weil es nichts gibt, in das eingewilligt werden müsste. Buchungsdaten werden weder für Werbung oder
        Marketingprofile noch für das Training von KI-Modellen verwendet.
      </p>

      <h2>1. Verantwortlicher</h2>
      <p>Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) für die Website evercal.de und die Datenverarbeitungen im Zusammenhang mit EverCal ist:</p>
      {controller}
      <p>
        Telefon: <a href="tel:+498924413422">+49 (0) 89 244 134 22</a>
        <br />
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
        <br />
        Web:{' '}
        <a href="https://www.erfolgsmagneten.com" rel="noopener">
          www.erfolgsmagneten.com
        </a>
      </p>
      <p>
        Registergericht: Amtsgericht München · Handelsregisternummer: HRB 295540
        <br />
        Geschäftsführer: Rainer Stolte
        <br />
        Umsatzsteuer-Identifikationsnummer: DE370363054
      </p>
      <p>EverCal ist ein Projekt der Erfolgsmagneten GmbH.</p>

      <h2>2. Website und Buchungsseiten</h2>
      <h3>Öffentliche Website</h3>
      <p>
        Die Website evercal.de dient der Information über EverCal. Auf der Website werden keine Cookies gesetzt und keine Analyse- oder Marketingdienste
        eingesetzt.
      </p>
      <h3>Buchungsseiten</h3>
      <p>
        Unter Adressen wie evercal.de/praxipet oder evercal.de/erfolgsmagneten stehen Buchungsseiten, über die Interessenten einen Termin mit uns
        vereinbaren – zum Beispiel eine Vorführung der Praxis-Software PraxiPet oder ein Erstgespräch mit Erfolgsmagneten. Diese Buchungsseiten betreibt
        die Erfolgsmagneten GmbH zurzeit ausschließlich für eigene Termine; eine Anmeldung Dritter ist nicht möglich. Für die Verarbeitung der dort
        eingegebenen Daten ist deshalb die Erfolgsmagneten GmbH verantwortlich.
      </p>

      <h2>3. Hosting und Datensicherung</h2>
      <p>Unsere Website sowie die technische Infrastruktur von EverCal werden bei der</p>
      <p>
        IONOS SE
        <br />
        Elgendorfer Straße 57
        <br />
        56410 Montabaur
        <br />
        Deutschland
      </p>
      <p>
        auf Servern in Deutschland betrieben. IONOS stellt uns Server-, Hosting- und Speicherleistungen zur Verfügung. Tägliche Sicherungskopien der
        Datenbank werden nach 14 Tagen automatisch gelöscht.
      </p>
      <p>
        Beim Aufruf unserer Website und der Buchungsseiten werden technisch erforderliche Informationen verarbeitet: IP-Adresse des aufrufenden Geräts,
        Datum und Uhrzeit des Zugriffs, aufgerufene Adresse, Browsertyp und -version sowie technische Statusinformationen. Diese Angaben stehen in
        Server-Protokollen, die fortlaufend überschrieben werden und in der Regel nur wenige Tage vorhanden sind.
      </p>
      <p>
        Die Verarbeitung ist erforderlich, um Website und Buchungsseiten technisch bereitzustellen, einen sicheren Betrieb zu gewährleisten, Fehler zu
        erkennen und Angriffe oder missbräuchliche Zugriffe abzuwehren. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt im
        sicheren, stabilen und zuverlässigen Betrieb unserer Website und unserer technischen Systeme. Soweit IONOS personenbezogene Daten in unserem Auftrag
        verarbeitet, erfolgt dies auf Grundlage von Art. 28 DSGVO.
      </p>

      <h2>4. TLS-Verschlüsselung</h2>
      <p>
        Unsere Website und die Buchungsseiten werden ausschließlich verschlüsselt übertragen (TLS). Eine verschlüsselte Verbindung erkennen Sie daran, dass
        die Adresse im Browser mit „https://“ beginnt.
      </p>

      <h2>5. Cookies und Speicherung im Browser</h2>
      <p>Auf der öffentlichen Website und auf den Buchungsseiten setzen wir keine Cookies und speichern nichts im Browser. Ein Cookie-Banner ist deshalb nicht erforderlich.</p>
      <p>
        Nur im geschützten Verwaltungsbereich, in dem wir Zeiten freigeben und Buchungen verwalten, setzen wir nach der Anmeldung ein technisch
        erforderliches Sitzungs-Cookie. Der Zugriff auf das Endgerät erfolgt auf Grundlage von § 25 Abs. 2 TDDDG, die damit verbundene Verarbeitung
        personenbezogener Daten auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.
      </p>
      <p>Statistik-, Reichweiten- oder Marketingtechnologien setzen wir nicht ein.</p>

      <h2>6. Terminbuchung</h2>
      <p>
        Wenn Sie über eine Buchungsseite einen Termin vereinbaren, verarbeiten wir die Angaben aus dem Buchungsformular: Name, E-Mail-Adresse, den
        gewählten Termin und die Terminart sowie – soweit Sie sie angeben – Telefonnummer, Praxis oder Firma und Ihre Nachricht. Diese Daten speichern wir
        in unserer Datenbank, um den Termin durchzuführen, Sie zu bestätigen, zu erinnern und bei Änderungen zu informieren.
      </p>
      <p>
        Die Verarbeitung dient der Anbahnung und Durchführung eines Vertrages beziehungsweise der Beantwortung Ihrer Anfrage und erfolgt auf Grundlage von
        Art. 6 Abs. 1 lit. b DSGVO.
      </p>
      <p>
        Mit der Bestätigung erhalten Sie persönliche Links, über die Sie den Termin absagen oder umbuchen können. Diese Links enthalten eine zufällige
        Kennung und keine personenbezogenen Daten.
      </p>
      <h3>Terminwunsch</h3>
      <p>
        Kann die Buchungsseite vorübergehend keine freien Zeiten anzeigen (etwa weil der Google-Kalender nicht antwortet), können Sie Name und E-Mail-Adresse
        hinterlassen. Wir speichern diese Angaben, um Ihnen den Link zur Terminwahl zu schicken, sobald sie wieder verfügbar ist, und um Ihnen bei Bedarf zu
        antworten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO.
      </p>
      <h3>Schutz vor automatisierten Buchungen</h3>
      <p>
        Um automatisierte Buchungen abzuwehren, zählen wir Anfragen je IP-Adresse für die Dauer einer Stunde im Arbeitsspeicher, prüfen die Schreibweise
        und den Mail-Server Ihrer E-Mail-Adresse und setzen ein für Menschen unsichtbares Formularfeld sowie eine Mindestzeit zwischen Aufruf und Absenden
        ein. Dafür werden keine Cookies gesetzt und keine Dienste Dritter eingesetzt; eine dauerhafte Speicherung der IP-Adresse zu einer Buchung erfolgt
        nicht. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse besteht im Schutz der Buchungsseiten vor Missbrauch.
      </p>

      <h2>7. Google Kalender und Google Meet</h2>
      <p>Für den Kalender und die Videogespräche nutzen wir Dienste der</p>
      <p>
        Google Ireland Limited
        <br />
        Gordon House, Barrow Street
        <br />
        Dublin 4
        <br />
        Irland
      </p>
      <p>
        Jede Buchung wird als Termin in unserem Google-Kalender eingetragen. In diesen Termin schreiben wir Ihren Namen und Ihre E-Mail-Adresse, den Termin
        und die Terminart; Telefonnummer, Firma und Nachricht bleiben in unserer Datenbank. Sie werden nicht als Gast in den Google-Termin aufgenommen und
        erhalten von Google keine Einladung. Bei Terminen per Videogespräch erzeugt Google einen Meet-Link, den wir Ihnen mit unserer Bestätigung schicken.
      </p>
      <p>
        Wenn Sie an einem Videogespräch über Google Meet teilnehmen, verarbeitet Google dabei Daten nach seinen eigenen Datenschutzbestimmungen
        (policies.google.com/privacy). Sie können stattdessen jederzeit einen Termin per Telefon wählen, soweit die Terminart das anbietet.
      </p>
      <p>
        Außerdem liest EverCal aus unseren eigenen Google-Kalendern, von wann bis wann wir belegt sind, um diese Zeiten auf der Buchungsseite nicht
        anzubieten. Dabei werden keine Daten von Ihnen verarbeitet.
      </p>
      <p>
        Rechtsgrundlage für die Übermittlung an Google ist Art. 6 Abs. 1 lit. b DSGVO (Durchführung des gebuchten Termins). Google kann Daten auch in den
        USA verarbeiten; Google LLC ist unter dem EU-US Data Privacy Framework zertifiziert, sodass für diese Übermittlung ein Angemessenheitsbeschluss der
        Europäischen Kommission gilt (Art. 45 DSGVO).
      </p>

      <h2>8. E-Mail-Versand über Brevo</h2>
      <p>Für den Versand der E-Mails aus EverCal (Bestätigung, Erinnerungen, Absage, Umbuchung, Zugangslink, Benachrichtigungen an uns) setzen wir die</p>
      <p>
        Brevo (Sendinblue GmbH)
        <br />
        Köpenicker Straße 126
        <br />
        10179 Berlin
        <br />
        Deutschland
      </p>
      <p>
        als Dienstleister ein. An Brevo werden die für die Zustellung erforderlichen Daten übermittelt, insbesondere E-Mail-Adresse, Name, Betreff und Inhalt
        der Nachricht samt Kalenderdatei. Brevo ist vertraglich nach Art. 28 DSGVO gebunden und verarbeitet die Daten in der Europäischen Union.
      </p>

      <h2>9. KI-Assistent des Betreibers</h2>
      <p>
        EverCal enthält selbst keine KI. Wir können jedoch einen KI-Assistenten (zum Beispiel Claude von Anthropic) mit unserer Verwaltung verbinden, um
        Zeiten zu sperren oder Buchungen anzusehen und abzusagen. Fragen wir dabei Buchungen ab, werden die betreffenden Daten (Name, E-Mail-Adresse,
        Termin, Terminart, Nachricht) an den Anbieter des Assistenten übermittelt. Jede solche Abfrage und Änderung wird protokolliert. Rechtsgrundlage ist
        Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse besteht in der effizienten Verwaltung unserer Termine. Ihre Daten werden dabei nicht zum
        Training von KI-Modellen verwendet.
      </p>

      <h2>10. Kein Werbetracking</h2>
      <p>
        Weder auf evercal.de noch auf den Buchungsseiten setzen wir Werbetracking ein, insbesondere kein Meta-Pixel und keine Webanalyse. Buchungsdaten
        werden nicht dazu verwendet, Werbeprofile zu erstellen, personalisierte Werbung auszuspielen oder Marketingzielgruppen aufzubauen.
      </p>

      <h2>11. Empfänger und Dienstleister</h2>
      <p>
        Wir geben personenbezogene Daten nur weiter, wenn hierfür eine Rechtsgrundlage besteht oder dies zur Erbringung unserer Leistungen erforderlich ist.
        Empfänger können Hosting- und IT-Dienstleister (IONOS SE), der Dienstleister für den E-Mail-Versand (Brevo / Sendinblue GmbH), Google (Kalender und
        Meet), der Anbieter eines von uns verbundenen KI-Assistenten, Steuerberater sowie Behörden sein, soweit wir gesetzlich zu einer Übermittlung
        verpflichtet sind. Dienstleister, die Daten ausschließlich in unserem Auftrag verarbeiten, werden entsprechend Art. 28 DSGVO eingebunden.
      </p>

      <h2>12. Datenübermittlungen außerhalb der EU und des EWR</h2>
      <p>
        IONOS und Brevo verarbeiten die Daten in Deutschland beziehungsweise in der Europäischen Union. Google und der Anbieter eines verbundenen
        KI-Assistenten können Daten auch in den USA verarbeiten; diese Übermittlungen stützen sich auf den Angemessenheitsbeschluss zum EU-US Data Privacy
        Framework (Art. 45 DSGVO) beziehungsweise auf Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).
      </p>

      <h2>13. Speicherdauer</h2>
      <p>Wir speichern personenbezogene Daten nur so lange, wie dies für den jeweiligen Verarbeitungszweck erforderlich ist.</p>
      <p>
        Buchungsdaten bleiben gespeichert, solange sie für die Durchführung und Nachbereitung des Termins benötigt werden; danach löschen wir sie im Rahmen
        regelmäßiger Durchsicht oder auf Ihre Anfrage. Terminwünsche löschen wir, sobald sie erledigt sind. Vertrags- und Abrechnungsdaten werden entsprechend
        den handels- und steuerrechtlichen Aufbewahrungspflichten gespeichert. Sicherungskopien werden nach 14 Tagen überschrieben.
      </p>

      <h2>14. Widerspruch gegen Verarbeitung aufgrund berechtigter Interessen</h2>
      <p>
        Soweit wir personenbezogene Daten auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO verarbeiten, haben Sie nach Art. 21 DSGVO das Recht, aus Gründen, die
        sich aus Ihrer besonderen Situation ergeben, jederzeit Widerspruch gegen diese Verarbeitung einzulegen. Wir verarbeiten die betreffenden Daten
        anschließend nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte und
        Freiheiten überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.
      </p>

      <h2>15. Ihre Datenschutzrechte</h2>
      <p>
        Nach Maßgabe der gesetzlichen Voraussetzungen haben Sie das Recht auf Auskunft gemäß Art. 15 DSGVO, Berichtigung gemäß Art. 16 DSGVO, Löschung gemäß
        Art. 17 DSGVO, Einschränkung der Verarbeitung gemäß Art. 18 DSGVO sowie Datenübertragbarkeit gemäß Art. 20 DSGVO. Zur Wahrnehmung Ihrer Rechte
        können Sie sich an uns wenden:
      </p>
      {controller}
      <p>
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
      </p>
      <p>Sie haben außerdem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren, etwa beim Bayerischen Landesamt für Datenschutzaufsicht.</p>

      <h2>16. Änderungen dieser Datenschutzerklärung</h2>
      <p>Wir passen diese Datenschutzerklärung an, wenn sich EverCal oder die Rechtslage ändert. Es gilt die jeweils hier veröffentlichte Fassung.</p>
    </>
  );
}
